import { useEffect, useState, useRef, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import {motion} from "framer-motion";
import { 
  Play, 
  Pause, 
  Square, 
  RotateCcw, 
  Settings as SettingsIcon, 
  Sun, 
  Moon, 
  Coffee, 
  Brain, 
  MoonStar, 
  Flame, 
  Clock, 
  Target, 
  Sparkles, 
  X,
  Volume2,
  Bell,
  Check,
  HelpCircle
} from "lucide-react";
import confetti from "canvas-confetti";
import { playAlarm, ALARMS } from "./utils/audioHelper";
import { getRandomQuote } from "./utils/quotes";
import ParticleBackground from "./components/ParticleBackground";
import MusicWidget from "./components/MusicWidget";

function App() {
  // --- STATE INITIALIZATION (Local Storage) ---
  const [sessionLength, setSessionLength] = useState(() => 
    Number(localStorage.getItem("cozy_session_length")) || 25
  );
  const [breakLength, setBreakLength] = useState(() => 
    Number(localStorage.getItem("cozy_break_length")) || 5
  );
  const [longBreakLength, setLongBreakLength] = useState(() => 
    Number(localStorage.getItem("cozy_long_break_length")) || 15
  );
  
  const [mode, setMode] = useState("focus"); // 'focus' | 'short-break' | 'long-break'
  
  const [totalSec, setTotalSec] = useState(() => {
    const savedMode = localStorage.getItem("cozy_current_mode") || "focus";
    if (savedMode === "focus") return (Number(localStorage.getItem("cozy_session_length")) || 25) * 60;
    if (savedMode === "short-break") return (Number(localStorage.getItem("cozy_break_length")) || 5) * 60;
    return (Number(localStorage.getItem("cozy_long_break_length")) || 15) * 60;
  });

  const [pause, setPause] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  
  // Settings Panel States
  const [selectedAlarm, setSelectedAlarm] = useState(() => 
    localStorage.getItem("cozy_selected_alarm") || "classic-bell"
  );
  const [volume, setVolume] = useState(() => 
    Number(localStorage.getItem("cozy_volume")) || 0.5
  );
  const [autoStartBreak, setAutoStartBreak] = useState(() => 
    localStorage.getItem("cozy_auto_start_break") === "true"
  );
  const [autoStartFocus, setAutoStartFocus] = useState(() => 
    localStorage.getItem("cozy_auto_start_focus") === "true"
  );
  const [enableNotifications, setEnableNotifications] = useState(() => 
    localStorage.getItem("cozy_notifications") === "true"
  );

  // Immersive Background Configs
  const [particleType, setParticleType] = useState(() => 
    localStorage.getItem("cozy_particle_type") || "fireflies"
  );
  const [bgBlur, setBgBlur] = useState(() => 
    Number(localStorage.getItem("cozy_bg_blur")) || 0
  );
  const [animationsEnabled, setAnimationsEnabled] = useState(() => 
    localStorage.getItem("cozy_animations_enabled") !== "false"
  );
  const [particlesEnabled, setParticlesEnabled] = useState(() => 
    localStorage.getItem("cozy_particles_enabled") !== "false"
  );

  // Statistics States
  const [completedSessions, setCompletedSessions] = useState(() => 
    Number(localStorage.getItem("cozy_completed_sessions")) || 0
  );
  const [focusTimeToday, setFocusTimeToday] = useState(() => 
    Number(localStorage.getItem("cozy_focus_time_today")) || 0
  );
  const [streak, setStreak] = useState(() => 
    Number(localStorage.getItem("cozy_streak")) || 0
  );
  const [dailyGoal, setDailyGoal] = useState(() => 
    Number(localStorage.getItem("cozy_daily_goal")) || 4
  );

  // Quote State
  const [quote, setQuote] = useState(() => getRandomQuote());
  
  // Toast notifications State
  const [toast, setToast] = useState(null);

  // Parallax mouse position
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // References
  const intervalRef = useRef(null);

  // --- PARALLAX TRACKER EFFECT ---
  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientWidth, clientHeight } = document.documentElement;
      const x = (e.clientX / clientWidth) - 0.5;
      const y = (e.clientY / clientHeight) - 0.5;
      setMousePos({ x, y });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // --- DERIVED VALUES ---
  const getCurrentDuration = useCallback(() => {
    if (mode === "focus") return sessionLength;
    if (mode === "short-break") return breakLength;
    return longBreakLength;
  }, [mode, sessionLength, breakLength, longBreakLength]);

  const totalDurationSec = getCurrentDuration() * 60;
  const progressPercent = totalDurationSec > 0 ? (totalSec / totalDurationSec) * 100 : 0;

  // --- TOAST TRIGGER ---
  const showToast = useCallback((message, duration = 3000) => {
    const id = Date.now();
    setToast({ id, message });
    setTimeout(() => {
      setToast(prev => prev && prev.id === id ? null : prev);
    }, duration);
  }, []);

  // --- STREAK LOGIC ---
  const updateStreak = useCallback(() => {
    const todayStr = new Date().toDateString();
    const lastDate = localStorage.getItem("cozy_last_completed_date");
    const currentStreak = Number(localStorage.getItem("cozy_streak")) || 0;

    if (lastDate === todayStr) {
      return; // Already completed a session today
    }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toDateString();

    let nextStreak = 1;
    if (lastDate === yesterdayStr) {
      nextStreak = currentStreak + 1;
    }

    setStreak(nextStreak);
    localStorage.setItem("cozy_streak", nextStreak.toString());
    localStorage.setItem("cozy_last_completed_date", todayStr);
  }, []);

  // --- DESKTOP NOTIFICATION ---
  const requestNotificationPermission = async () => {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        setEnableNotifications(true);
        localStorage.setItem("cozy_notifications", "true");
        showToast("Desktop notifications enabled! 🔔");
      } else {
        setEnableNotifications(false);
        localStorage.setItem("cozy_notifications", "false");
        showToast("Notifications permission denied.");
      }
    }
  };

  const sendDesktopNotification = useCallback((title, body) => {
    if (enableNotifications && "Notification" in window && Notification.permission === "granted") {
      new Notification(title, { body, icon: "/vite.svg" });
    }
  }, [enableNotifications]);

  const rotateQuote = useCallback(() => {
    setQuote(getRandomQuote(quote));
  }, [quote]);

  // --- HANDLE TIMER COMPLETE ---
  const handleTimerComplete = useCallback(() => {
    playAlarm(selectedAlarm, volume);

    if (mode === "focus") {
      // Trigger Confetti!
      if (animationsEnabled) {
        triggerConfetti();
      }

      // Update completed sessions
      const nextCompleted = completedSessions + 1;
      setCompletedSessions(nextCompleted);
      localStorage.setItem("cozy_completed_sessions", nextCompleted.toString());

      // Update focus time today
      const nextTime = focusTimeToday + sessionLength;
      setFocusTimeToday(nextTime);
      localStorage.setItem("cozy_focus_time_today", nextTime.toString());

      // Update streak
      updateStreak();

      showToast("Beautiful focus! Time to take a cozy break. ☕");
      rotateQuote();
      sendDesktopNotification("Focus completed!", "Time to take a cozy break. ☕");

      // Auto transition
      const isLongBreak = nextCompleted % 4 === 0;
      const nextMode = isLongBreak ? "long-break" : "short-break";
      setMode(nextMode);
      localStorage.setItem("cozy_current_mode", nextMode);

      const nextDuration = (isLongBreak ? longBreakLength : breakLength) * 60;
      setTotalSec(nextDuration);
      setPause(!autoStartBreak);
    } else {
      // Completed break
      showToast("Break finished! Ready to focus? 📚");
      sendDesktopNotification("Break over!", "Ready to focus? Let's go. 📚");

      setMode("focus");
      localStorage.setItem("cozy_current_mode", "focus");
      setTotalSec(sessionLength * 60);
      setPause(!autoStartFocus);
    }
  }, [
    selectedAlarm,
    volume,
    mode,
    animationsEnabled,
    completedSessions,
    sessionLength,
    focusTimeToday,
    longBreakLength,
    breakLength,
    autoStartBreak,
    autoStartFocus,
    updateStreak,
    showToast,
    rotateQuote,
    sendDesktopNotification
  ]);

  // --- TIMER LOGIC (INTERVAL) ---
  useEffect(() => {
    if (pause) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(() => {
      setTotalSec(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          handleTimerComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [pause, handleTimerComplete]);

  // Sync tab switching
  const handleModeChange = (newMode) => {
    setPause(true);
    setMode(newMode);
    localStorage.setItem("cozy_current_mode", newMode);
    
    let duration = 25;
    if (newMode === "focus") duration = sessionLength;
    else if (newMode === "short-break") duration = breakLength;
    else duration = longBreakLength;

    setTotalSec(duration * 60);
    showToast(`Switched to ${newMode.replace("-", " ")} mode`);
  };

  // --- CONFETTI EFFECT ---
  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ["#FFB86C", "#D6A76A", "#FAF6EE", "#8D7B68"]
    });
  };

  const handleReset = useCallback(() => {
    setPause(true);
    setTotalSec(getCurrentDuration() * 60);
    showToast("Timer reset");
  }, [getCurrentDuration, showToast]);

  // --- KEYBOARD SHORTCUTS ---

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) {
        return;
      }

      const key = e.key.toLowerCase();

      if (e.code === "Space") {
        e.preventDefault();
        setPause(prev => !prev);
        showToast(pause ? "Timer resumed ☀️" : "Timer paused ☕", 1000);
      } else if (key === "r") {
        e.preventDefault();
        handleReset();
        showToast("Timer reset ↺", 1000);
      } else if (key === "s") {
        e.preventDefault();
        setPause(true);
        setTotalSec(getCurrentDuration() * 60);
        showToast("Timer stopped ■", 1000);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pause, handleReset, getCurrentDuration, showToast]);

  // --- CONTROL BUTTON ACTIONS ---
  const togglePlay = () => {
    setPause(prev => !prev);
  };

  const handleStop = useCallback(() => {
    setPause(true);
    setTotalSec(getCurrentDuration() * 60);
    showToast("Timer stopped");
  }, [getCurrentDuration, showToast]);

  

  // const rotateQuote = () => {
  //   setQuote(getRandomQuote(quote));
  // };

  // --- SETTINGS SETTERS ---
  const handleSessionLengthChange = (val) => {
    const num = Math.max(1, Math.min(120, Number(val) || 25));
    setSessionLength(num);
    localStorage.setItem("cozy_session_length", num.toString());
    if (mode === "focus" && pause) {
      setTotalSec(num * 60);
    }
  };

  const handleBreakLengthChange = (val) => {
    const num = Math.max(1, Math.min(60, Number(val) || 5));
    setBreakLength(num);
    localStorage.setItem("cozy_break_length", num.toString());
    if (mode === "short-break" && pause) {
      setTotalSec(num * 60);
    }
  };

  const handleLongBreakLengthChange = (val) => {
    const num = Math.max(1, Math.min(60, Number(val) || 15));
    setLongBreakLength(num);
    localStorage.setItem("cozy_long_break_length", num.toString());
    if (mode === "long-break" && pause) {
      setTotalSec(num * 60);
    }
  };

  const handleAlarmChange = (alarmId) => {
    setSelectedAlarm(alarmId);
    localStorage.setItem("cozy_selected_alarm", alarmId);
    playAlarm(alarmId, volume); // Preview sound on change
  };

  const handleVolumeChange = (val) => {
    const vol = parseFloat(val);
    setVolume(vol);
    localStorage.setItem("cozy_volume", vol.toString());
  };

  const toggleAutoStartBreak = () => {
    const next = !autoStartBreak;
    setAutoStartBreak(next);
    localStorage.setItem("cozy_auto_start_break", next.toString());
    showToast(next ? "Auto-start breaks: ON" : "Auto-start breaks: OFF");
  };

  const toggleAutoStartFocus = () => {
    const next = !autoStartFocus;
    setAutoStartFocus(next);
    localStorage.setItem("cozy_auto_start_focus", next.toString());
    showToast(next ? "Auto-start focus: ON" : "Auto-start focus: OFF");
  };

  const toggleNotifications = () => {
    if (!enableNotifications) {
      requestNotificationPermission();
    } else {
      setEnableNotifications(false);
      localStorage.setItem("cozy_notifications", "false");
      showToast("Notifications disabled.");
    }
  };

  // Extra Cozy Config Setters
  const handleBgBlurChange = (val) => {
    const blur = Number(val);
    setBgBlur(blur);
    localStorage.setItem("cozy_bg_blur", blur.toString());
  };

  const handleParticleTypeChange = (type) => {
    setParticleType(type);
    localStorage.setItem("cozy_particle_type", type);
    showToast(`Ambience set to ${type}`);
  };

  const toggleParticlesEnabled = () => {
    const next = !particlesEnabled;
    setParticlesEnabled(next);
    localStorage.setItem("cozy_particles_enabled", next.toString());
    showToast(next ? "Ambience effects: ON" : "Ambience effects: OFF");
  };

  const toggleAnimationsEnabled = () => {
    const next = !animationsEnabled;
    setAnimationsEnabled(next);
    localStorage.setItem("cozy_animations_enabled", next.toString());
    showToast(next ? "UI Animations: ON" : "UI Animations: OFF");
  };

  // --- TIME DISPLAY HELPER ---
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  // Update Document Title
  useEffect(() => {
    const modeLabel = mode === "focus" ? "Focus" : "Break";
    document.title = pause ? "Cozy Timer ☕" : `(${formatTime(totalSec)}) ${modeLabel} | Cozy Timer`;
  }, [totalSec, pause, mode]);

  // Radius values for the progress circle SVG
  const radius = 135;
  const strokeWidth = 5;
  const normalizedRadius = radius - strokeWidth * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (progressPercent / 100) * circumference;

  // Spring transition constants
  const springTransition = animationsEnabled 
    ? { type: "spring", stiffness: 220, damping: 25 } 
    : { duration: 0 };

  return (
    <div className="min-h-screen relative overflow-hidden grain-overlay select-none bg-cozy-navy text-dark-text-primary">
      
      {/* Immersive Background Image with Mouse Parallax */}
      <div 
        className="fixed inset-0 z-0 w-full h-full bg-cover bg-center"
        style={{
          backgroundImage: "url('/cozy_study_room.jpg')",
          transform: `scale(1.05) translate(${mousePos.x * -15}px, ${mousePos.y * -15}px)`,
          transition: "transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
        }}
      />
      
      {/* Background Dark Overlay & Blur */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none transition-all duration-300"
        style={{
          backgroundColor: "rgba(10, 11, 18, 0.52)", // 52% Dark overlay for readability
          backdropFilter: `blur(${bgBlur}px)`,
          WebkitBackdropFilter: `blur(${bgBlur}px)`,
        }}
      />

      {/* Lightweight Ambience Particles */}
      <ParticleBackground type={particleType} enabled={particlesEnabled} />

      {/* Main Header / Navigation */}
      <header className="relative z-10 max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold tracking-tight text-dark-text-primary flex items-center gap-1.5 drop-shadow">
            cozy pomodoro
          </span>
        </div>
        
        {/* Settings Trigger Button */}
        <motion.button 
          onClick={() => setIsSettingsOpen(true)}
          whileHover={animationsEnabled ? { scale: 1.06 } : {}}
          whileTap={animationsEnabled ? { scale: 0.95 } : {}}
          className="p-2.5 rounded-full bg-dark-bg/60 border border-white/10 hover:border-[#FFB86C]/40 text-dark-text-secondary hover:text-cozy-orange shadow-lg backdrop-blur-md transition duration-200 cursor-pointer pointer-events-auto"
          aria-label="Open Settings"
        >
          <SettingsIcon className="w-5 h-5" />
        </motion.button>
      </header>

      {/* Main Content Layout */}
      <main className="relative z-10 max-w-4xl mx-auto px-6 py-6 flex flex-col items-center justify-center min-h-[calc(100vh-140px)]">
        
        {/* The Pomodoro Timer Card (macOS glassmorphism style) */}
        <motion.div 
          initial={animationsEnabled ? { opacity: 0, y: 15 } : {}}
          animate={animationsEnabled ? { opacity: 1, y: 0 } : {}}
          transition={springTransition}
          className="w-full max-w-md bg-dark-bg/60 border border-white/10 rounded-[32px] p-6 sm:p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.65)] backdrop-blur-xl cozy-floating-card border-t-[#FFB86C]/10 border-r-[#FFB86C]/5 shadow-[#FFB86C]/2 text-dark-text-primary select-none flex flex-col items-center"
        >
          {/* Greeting/Title & Motivational Quote */}
          <div className="text-center mb-6">
            <h2 className="text-lg font-semibold tracking-tight text-[#FAF6EE] drop-shadow-sm flex items-center justify-center gap-2">
              Stay Present <Sparkles className="w-4 h-4 text-cozy-orange" />
            </h2>
            <p className="mt-1.5 text-xs sm:text-sm font-serif italic text-dark-text-secondary/90 px-4">
              "{quote}"
            </p>
          </div>

          {/* Session Modes Selection Tabs */}
          <div className="w-full flex bg-black/30 p-1.5 rounded-2xl mb-7 relative border border-white/5">
            <button
              onClick={() => handleModeChange("focus")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs sm:text-sm font-semibold rounded-xl transition-all duration-200 cursor-pointer relative z-10 ${
                mode === "focus" 
                  ? "text-cozy-orange font-bold" 
                  : "text-dark-text-secondary hover:text-dark-text-primary"
              }`}
            >
              <Brain className="w-4 h-4" /> Focus
              {mode === "focus" && (
                <motion.div
                  layoutId="activeTabUnderline"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FFB86C] rounded-full"
                  transition={springTransition}
                />
              )}
            </button>
            <button
              onClick={() => handleModeChange("short-break")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs sm:text-sm font-semibold rounded-xl transition-all duration-200 cursor-pointer relative z-10 ${
                mode === "short-break" 
                  ? "text-cozy-orange font-bold" 
                  : "text-dark-text-secondary hover:text-dark-text-primary"
              }`}
            >
              <Coffee className="w-4 h-4" /> Break
              {mode === "short-break" && (
                <motion.div
                  layoutId="activeTabUnderline"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FFB86C] rounded-full"
                  transition={springTransition}
                />
              )}
            </button>
            <button
              onClick={() => handleModeChange("long-break")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs sm:text-sm font-semibold rounded-xl transition-all duration-200 cursor-pointer relative z-10 ${
                mode === "long-break" 
                  ? "text-cozy-orange font-bold" 
                  : "text-dark-text-secondary hover:text-dark-text-primary"
              }`}
            >
              <MoonStar className="w-4 h-4" /> Long Break
              {mode === "long-break" && (
                <motion.div
                  layoutId="activeTabUnderline"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FFB86C] rounded-full"
                  transition={springTransition}
                />
              )}
            </button>
          </div>

          {/* Circular Progress Ring + Timer Numbers */}
          <div className="flex flex-col items-center justify-center relative mb-7">
            <svg
              width={radius * 2}
              height={radius * 2}
              className={`transform -rotate-90 select-none drop-shadow-[0_0_15px_rgba(255,184,108,0.22)] ${
                !pause ? "animate-[pulse_4s_ease-in-out_infinite]" : ""
              }`}
            >
              {/* Track */}
              <circle
                stroke="currentColor"
                fill="transparent"
                strokeWidth={strokeWidth}
                r={normalizedRadius}
                cx={radius}
                cy={radius}
                className="text-white/5"
              />
              {/* Progress */}
              <motion.circle
                stroke="currentColor"
                fill="transparent"
                strokeWidth={strokeWidth}
                strokeDasharray={circumference + " " + circumference}
                style={{ strokeDashoffset }}
                r={normalizedRadius}
                cx={radius}
                cy={radius}
                className="text-[#FFB86C] transition-all duration-1000 ease-linear"
                strokeLinecap="round"
              />
            </svg>

            {/* Time readout */}
            <div className="absolute flex flex-col items-center justify-center text-center">
              <span className={`text-5xl sm:text-6xl font-bold tracking-tighter text-[#FAF6EE] drop-shadow-md select-none ${
                !pause ? "animate-pulse" : ""
              }`}>
                {formatTime(totalSec)}
              </span>
              <span className="text-[10px] sm:text-xs font-semibold tracking-widest uppercase text-cozy-orange/80 mt-1.5 drop-shadow-sm">
                {mode === "focus" ? "Stay focused" : "Rest & Refresh"}
              </span>
            </div>
          </div>

          {/* Action Control Buttons */}
          <div className="flex items-center justify-center gap-4.5">
            {/* Play/Pause Button */}
            <motion.button
              onClick={togglePlay}
              whileHover={animationsEnabled ? { scale: 1.05, boxShadow: "0 0 20px rgba(255, 184, 108, 0.35)" } : {}}
              whileTap={animationsEnabled ? { scale: 0.95 } : {}}
              className="flex items-center justify-center w-14 h-14 rounded-full bg-[#FFB86C] text-[#12131C] font-bold shadow-lg shadow-[#FFB86C]/10 active:scale-95 transition duration-200 cursor-pointer pointer-events-auto"
              aria-label={pause ? "Start Timer" : "Pause Timer"}
            >
              {pause ? <Play className="w-5.5 h-5.5 fill-current ml-0.5" /> : <Pause className="w-5.5 h-5.5 fill-current" />}
            </motion.button>

            {/* Stop Button */}
            {!pause && (
              <motion.button
                onClick={handleStop}
                whileHover={animationsEnabled ? { scale: 1.05 } : {}}
                whileTap={animationsEnabled ? { scale: 0.95 } : {}}
                className="flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/15 text-dark-text-secondary hover:text-[#FFB86C] border border-white/5 active:scale-95 transition duration-200 cursor-pointer pointer-events-auto"
                aria-label="Stop Timer"
              >
                <Square className="w-4 h-4 fill-current" />
              </motion.button>
            )}

            {/* Reset Button */}
            {pause && (
              <motion.button
                onClick={handleReset}
                whileHover={animationsEnabled ? { scale: 1.05 } : {}}
                whileTap={animationsEnabled ? { scale: 0.95 } : {}}
                className="flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/15 text-dark-text-secondary hover:text-[#FFB86C] border border-white/5 active:scale-95 transition duration-200 cursor-pointer pointer-events-auto"
                aria-label="Reset Timer"
              >
                <RotateCcw className="w-4 h-4" />
              </motion.button>
            )}
          </div>
        </motion.div>

        {/* Daily Stats Grid Section */}
        <section className="w-full max-w-2xl mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 pointer-events-auto select-none">
          <div className="bg-dark-bg/50 backdrop-blur-md border border-white/5 rounded-2xl p-4 flex flex-col items-center justify-center text-center shadow-md">
            <Flame className="w-5 h-5 text-cozy-orange mb-1.5" />
            <span className="text-xl font-bold text-dark-text-primary">{streak}</span>
            <span className="text-xs text-dark-text-secondary">Streak Days</span>
          </div>
          
          <div className="bg-dark-bg/50 backdrop-blur-md border border-white/5 rounded-2xl p-4 flex flex-col items-center justify-center text-center shadow-md">
            <Check className="w-5 h-5 text-cozy-orange mb-1.5" />
            <span className="text-xl font-bold text-dark-text-primary">{completedSessions}</span>
            <span className="text-xs text-dark-text-secondary">Sessions</span>
          </div>

          <div className="bg-dark-bg/50 backdrop-blur-md border border-white/5 rounded-2xl p-4 flex flex-col items-center justify-center text-center shadow-md">
            <Clock className="w-5 h-5 text-cozy-orange mb-1.5" />
            <span className="text-xl font-bold text-dark-text-primary">{focusTimeToday}m</span>
            <span className="text-xs text-dark-text-secondary">Focus Time</span>
          </div>

          <div className="bg-dark-bg/50 backdrop-blur-md border border-white/5 rounded-2xl p-4 flex flex-col items-center justify-center text-center shadow-md">
            <Target className="w-5 h-5 text-cozy-orange mb-1.5" />
            <span className="text-xl font-bold text-dark-text-primary">
              {completedSessions}/{dailyGoal}
            </span>
            <span className="text-xs text-dark-text-secondary">Daily Goal</span>
          </div>
        </section>
      </main>

      {/* Floating Music widget in bottom-left corner */}
      <div className="fixed bottom-5 left-5 z-20 hidden md:block">
        <MusicWidget />
      </div>

      {/* --- COLLAPSIBLE SETTINGS PANEL (DRAWER) --- */}
      <AnimatePresence>
        {isSettingsOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSettingsOpen(false)}
              className="fixed inset-0 z-40 bg-black/45 backdrop-blur-sm cursor-pointer pointer-events-auto"
            />
            
            {/* Drawer Container */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={springTransition}
              className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-[#12131C] border-l border-white/10 p-6 shadow-2xl overflow-y-auto flex flex-col pointer-events-auto"
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between pb-4 border-b border-white/10">
                <h2 className="text-lg font-bold text-dark-text-primary flex items-center gap-1.5">
                  cozy room settings <Sparkles className="w-4 h-4 text-cozy-orange" />
                </h2>
                <button
                  onClick={() => setIsSettingsOpen(false)}
                  className="p-1.5 rounded-full hover:bg-white/5 text-dark-text-secondary hover:text-dark-text-primary transition duration-150 cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Drawer Scrollable Content */}
              <div className="mt-6 flex-1 space-y-6">
                
                {/* 1. Timer Lengths (Number Input + Sliders) */}
                <div className="space-y-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-dark-text-secondary">
                    Timer Durations (minutes)
                  </h3>
                  
                  {/* Focus Duration */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <label className="text-dark-text-primary font-medium">Focus Duration</label>
                      <span className="font-semibold text-cozy-orange">{sessionLength}m</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <input 
                        type="range" 
                        min="1" 
                        max="120"
                        value={sessionLength}
                        onChange={(e) => handleSessionLengthChange(e.target.value)}
                        className="flex-1 accent-cozy-orange h-1 bg-white/10 rounded-lg cursor-pointer"
                      />
                      <div className="flex items-center bg-black/45 rounded border border-white/10">
                        <button 
                          onClick={() => handleSessionLengthChange(sessionLength - 1)}
                          className="px-2.5 py-1 text-dark-text-secondary hover:text-dark-text-primary cursor-pointer text-sm font-bold"
                        >
                          -
                        </button>
                        <input 
                          type="number" 
                          min="1" 
                          max="120"
                          value={sessionLength}
                          onChange={(e) => handleSessionLengthChange(e.target.value)}
                          className="w-10 bg-transparent text-center text-sm font-semibold text-dark-text-primary focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <button 
                          onClick={() => handleSessionLengthChange(sessionLength + 1)}
                          className="px-2.5 py-1 text-dark-text-secondary hover:text-dark-text-primary cursor-pointer text-sm font-bold"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Short Break Length */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <label className="text-dark-text-primary font-medium">Short Break</label>
                      <span className="font-semibold text-cozy-orange">{breakLength}m</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <input 
                        type="range" 
                        min="1" 
                        max="60"
                        value={breakLength}
                        onChange={(e) => handleBreakLengthChange(e.target.value)}
                        className="flex-1 accent-cozy-orange h-1 bg-white/10 rounded-lg cursor-pointer"
                      />
                      <div className="flex items-center bg-black/45 rounded border border-white/10">
                        <button 
                          onClick={() => handleBreakLengthChange(breakLength - 1)}
                          className="px-2.5 py-1 text-dark-text-secondary hover:text-dark-text-primary cursor-pointer text-sm font-bold"
                        >
                          -
                        </button>
                        <input 
                          type="number" 
                          min="1" 
                          max="60"
                          value={breakLength}
                          onChange={(e) => handleBreakLengthChange(e.target.value)}
                          className="w-10 bg-transparent text-center text-sm font-semibold text-dark-text-primary focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <button 
                          onClick={() => handleBreakLengthChange(breakLength + 1)}
                          className="px-2.5 py-1 text-dark-text-secondary hover:text-dark-text-primary cursor-pointer text-sm font-bold"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Long Break Length */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <label className="text-dark-text-primary font-medium">Long Break</label>
                      <span className="font-semibold text-cozy-orange">{longBreakLength}m</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <input 
                        type="range" 
                        min="1" 
                        max="60"
                        value={longBreakLength}
                        onChange={(e) => handleLongBreakLengthChange(e.target.value)}
                        className="flex-1 accent-cozy-orange h-1 bg-white/10 rounded-lg cursor-pointer"
                      />
                      <div className="flex items-center bg-black/45 rounded border border-white/10">
                        <button 
                          onClick={() => handleLongBreakLengthChange(longBreakLength - 1)}
                          className="px-2.5 py-1 text-dark-text-secondary hover:text-dark-text-primary cursor-pointer text-sm font-bold"
                        >
                          -
                        </button>
                        <input 
                          type="number" 
                          min="1" 
                          max="60"
                          value={longBreakLength}
                          onChange={(e) => handleLongBreakLengthChange(e.target.value)}
                          className="w-10 bg-transparent text-center text-sm font-semibold text-dark-text-primary focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <button 
                          onClick={() => handleLongBreakLengthChange(longBreakLength + 1)}
                          className="px-2.5 py-1 text-dark-text-secondary hover:text-dark-text-primary cursor-pointer text-sm font-bold"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2. Alarm Selection */}
                <div className="space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-dark-text-secondary flex items-center gap-1.5">
                    Alarm Sound <Bell className="w-4 h-4" />
                  </h3>
                  <div className="grid grid-cols-1 gap-1.5">
                    {ALARMS.map((alarm) => (
                      <button
                        key={alarm.id}
                        onClick={() => handleAlarmChange(alarm.id)}
                        className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl border text-xs sm:text-sm font-semibold transition-all duration-200 cursor-pointer ${
                          selectedAlarm === alarm.id
                            ? "bg-cozy-orange/10 border-cozy-orange text-cozy-orange"
                            : "bg-black/25 border-white/5 text-dark-text-secondary hover:bg-white/5 hover:text-dark-text-primary"
                        }`}
                      >
                        <span>{alarm.name}</span>
                        <div className="flex items-center gap-2">
                          {selectedAlarm === alarm.id && <Check className="w-4 h-4" />}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              playAlarm(alarm.id, volume);
                            }}
                            className="px-2 py-0.5 text-[10px] font-semibold rounded bg-white/10 text-dark-text-secondary hover:text-dark-text-primary hover:bg-white/20 transition cursor-pointer"
                          >
                            Preview
                          </button>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 3. Audio Volume Control */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <label className="text-dark-text-primary font-medium flex items-center gap-1.5">
                      Alarm Volume <Volume2 className="w-4 h-4" />
                    </label>
                    <span className="font-semibold text-dark-text-primary">{Math.round(volume * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={volume}
                    onChange={(e) => handleVolumeChange(e.target.value)}
                    className="w-full accent-cozy-orange h-1 bg-white/10 rounded-lg cursor-pointer"
                  />
                </div>

                {/* 4. Immersive Ambient Settings (Blur, Particles, etc.) */}
                <div className="space-y-4 pt-2 border-t border-white/5">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-dark-text-secondary">
                    Immersive Ambience
                  </h3>

                  {/* Background Blur Strength */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <label className="text-dark-text-primary font-medium">Background Blur</label>
                      <span className="font-semibold text-cozy-orange">{bgBlur}px</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="20"
                      value={bgBlur}
                      onChange={(e) => handleBgBlurChange(e.target.value)}
                      className="w-full accent-cozy-orange h-1 bg-white/10 rounded-lg cursor-pointer"
                    />
                  </div>

                  {/* Particle Selection dropdown */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-dark-text-primary">Background Effects</label>
                    <select
                      value={particleType}
                      onChange={(e) => handleParticleTypeChange(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-black/45 border border-white/10 rounded-xl text-dark-text-primary focus:outline-none accent-black"
                    >
                      <option value="fireflies">🔥 Fireflies</option>
                      <option value="rain">🌧 Gentle Rain</option>
                      <option value="snow">❄️ Soft Snow</option>
                      <option value="leaves">🍃 Floating Leaves</option>
                      <option value="dust">✨ Dust Particles</option>
                      <option value="none">🚫 No effects</option>
                    </select>
                  </div>
                </div>

                {/* 5. Preference Toggle Toggles */}
                <div className="space-y-4 pt-2 border-t border-white/5">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-dark-text-secondary">
                    Preferences
                  </h3>
                  
                  {/* Auto-start break */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-dark-text-primary">Auto-start Breaks</span>
                      <span className="text-xs text-dark-text-secondary">Start breaks automatically</span>
                    </div>
                    <button
                      onClick={toggleAutoStartBreak}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-250 cursor-pointer ${
                        autoStartBreak ? "bg-cozy-orange" : "bg-white/10"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-250 ${
                          autoStartBreak ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  {/* Auto-start Focus */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-dark-text-primary">Auto-start Focus</span>
                      <span className="text-xs text-dark-text-secondary">Start focus automatically</span>
                    </div>
                    <button
                      onClick={toggleAutoStartFocus}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-250 cursor-pointer ${
                        autoStartFocus ? "bg-cozy-orange" : "bg-white/10"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-250 ${
                          autoStartFocus ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  {/* Desktop Notifications Toggle */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-dark-text-primary">Desktop Notifications</span>
                      <span className="text-xs text-dark-text-secondary">Receive browser popups</span>
                    </div>
                    <button
                      onClick={toggleNotifications}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-250 cursor-pointer ${
                        enableNotifications ? "bg-cozy-orange" : "bg-white/10"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-250 ${
                          enableNotifications ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  {/* Ambience Toggle */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-dark-text-primary">Ambience Effects</span>
                      <span className="text-xs text-dark-text-secondary">Enable rain, snow or firefly particles</span>
                    </div>
                    <button
                      onClick={toggleParticlesEnabled}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-250 cursor-pointer ${
                        particlesEnabled ? "bg-cozy-orange" : "bg-white/10"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-250 ${
                          particlesEnabled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  {/* UI Animations Toggle */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-dark-text-primary">UI Animations</span>
                      <span className="text-xs text-dark-text-secondary">Toggle Framer Motion effects</span>
                    </div>
                    <button
                      onClick={toggleAnimationsEnabled}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-250 cursor-pointer ${
                        animationsEnabled ? "bg-cozy-orange" : "bg-white/10"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-250 ${
                          animationsEnabled ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* 6. Streak / Daily Goal Configuration */}
                <div className="space-y-3 pt-2 border-t border-white/5">
                  <div className="flex justify-between text-sm">
                    <label className="text-dark-text-primary font-medium">Daily Goal (Sessions)</label>
                    <span className="font-semibold text-cozy-orange">{dailyGoal}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="1"
                      max="12"
                      value={dailyGoal}
                      onChange={(e) => {
                        const val = Math.max(1, Math.min(12, Number(e.target.value) || 4));
                        setDailyGoal(val);
                        localStorage.setItem("cozy_daily_goal", val.toString());
                      }}
                      className="w-full accent-cozy-orange h-1 bg-white/10 rounded-lg cursor-pointer"
                    />
                  </div>
                </div>

              </div>

              {/* Drawer Footer */}
              <div className="mt-8 pt-4 border-t border-white/10 text-center">
                <p className="text-xs text-dark-text-secondary">
                  Shortcut reminders:<br />
                  [Space] to Play/Pause &bull; [S] to Stop &bull; [R] to Reset
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* --- TOAST NOTIFICATIONS --- */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={animationsEnabled ? { opacity: 0, y: 40, scale: 0.95 } : {}}
            animate={animationsEnabled ? { opacity: 1, y: 0, scale: 1 } : {}}
            exit={animationsEnabled ? { opacity: 0, y: 15, scale: 0.95 } : {}}
            className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 px-4.5 py-2.5 rounded-full bg-[#12131C] text-[#FAF6EE] border border-white/10 text-xs sm:text-sm font-semibold shadow-2xl backdrop-blur-md flex items-center gap-2"
          >
            <span>{toast.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}

export default App;